import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { verifyResetOtp, requestForgotPassword } from "../api/login";

const VerifyResetOtp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const emailFromLocation = location.state?.email || "";

  const [email] = useState(emailFromLocation); 
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(120);
  const [canResend, setCanResend] = useState(false);
  const [error, setError] = useState("");

  const ref1 = useRef();
  const ref2 = useRef();
  const ref3 = useRef();
  const ref4 = useRef();
  const ref5 = useRef();
  const ref6 = useRef();

  const refs = [ref1, ref2, ref3, ref4, ref5, ref6];

  useEffect(() => {
    if (!email) {
      toast.error("Email missing. Please restart from Forgot Password.");
      navigate("/forgot-password");
    }
  }, [email, navigate]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(0, 1);
    setOtp(newOtp);
    setError("");
    if (value && index < 5) refs[index + 1].current.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      refs[index - 1].current.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6)
      .split("");
    const newOtp = [...otp];
    pasted.forEach((char, i) => {
      if (i < 6) newOtp[i] = char;
    });
    setOtp(newOtp);
    const next = newOtp.findIndex((val) => !val);
    if (next !== -1) refs[next].current.focus();
  };

  const handleResend = async () => {
    try {
      await requestForgotPassword(email);
      toast.success("OTP resent successfully");
      setOtp(["", "", "", "", "", ""]);
      setTimer(120);
      setCanResend(false);
      refs[0].current.focus();
    } catch {
      toast.error("Failed to resend OTP");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpString = otp.join("");
    if (otpString.length !== 6) return toast.error("Enter all 6 digits");

    try {
      const res = await verifyResetOtp(email, otpString);
      toast.success("OTP verified");
      navigate("/reset-password", { state: { email, token: res.token } });
    } catch (err) {
      const message = err.response?.data?.message || "Invalid OTP";
      setError(message);
      toast.error(message);
    }
  };

  const formatTime = (seconds) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, "0");
    const secs = String(seconds % 60).padStart(2, "0");
    return `${mins}:${secs}`;
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center py-16 px-4 text-white relative overflow-hidden">
      <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-purple-900/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyan-900/20 rounded-full blur-3xl animate-pulse"></div>

      <div className="relative z-10 max-w-md w-full p-8 rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 shadow-lg">
        <h2 className="text-3xl font-extrabold text-center mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">
          Verify OTP
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm mb-2 text-gray-400">Email</label>
            <input
              type="email"
              value={email}
              disabled
              className="w-full p-3 rounded-lg bg-black/50 border border-cyan-500 text-white placeholder-white/60"
            />
          </div>

          <div className="flex justify-between gap-2">
            {otp.map((val, idx) => (
              <input
                key={idx}
                ref={refs[idx]}
                type="text"
                value={val}
                onChange={(e) => handleChange(idx, e.target.value)}
                onKeyDown={(e) => handleKeyDown(idx, e)}
                onPaste={handlePaste}
                maxLength={1}
                className={`w-12 h-14 rounded-lg text-center text-xl bg-black/60 border ${
                  error ? "border-red-500" : "border-cyan-500"
                } text-white`}
              />
            ))}
          </div>

          {error && <p className="text-red-400 text-center text-sm">{error}</p>}

          <button
            type="submit"
            disabled={otp.join("").length !== 6}
            className="w-full py-3 rounded-lg bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-semibold hover:scale-105 transition-all"
          >
            Verify OTP
          </button>

          <p className="text-center text-sm text-gray-400 mt-4">
            Didn’t receive the code?{" "}
            <button
              type="button"
              onClick={handleResend}
              disabled={!canResend}
              className="text-cyan-400 hover:underline"
            >
              {canResend ? "Resend Code" : `Resend in ${formatTime(timer)}`}
            </button>
          </p>

          <p className="text-center mt-3">
            <Link to="/login" className="text-sm text-cyan-400 hover:underline">
              Back to Login
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default VerifyResetOtp;
